const OpeninghoursModel = (json, lang, translate) => {
    // Helper Functions

    /**
     * Calculates the date of Easter for a given year using the Meeus/Jones/Butcher algorithm.
     * @param {number} year - The year for which to calculate Easter.
     * @returns {Date} - The date of Easter.
     */
    const getEasterDate = (year) => {
        let a = year % 19;
        let b = Math.floor(year / 100);
        let c = year % 100;
        let d = Math.floor(b / 4);
        let e = b % 4;
        let f = Math.floor((b + 8) / 25);
        let g = Math.floor((b - f + 1) / 3);
        let h = (19 * a + b - d - g + 15) % 30;
        let i = Math.floor(c / 4);
        let k = c % 4;
        let L = (32 + 2 * e + 2 * i - h - k) % 7;
        let m = Math.floor((a + 11 * h + 22 * L) / 451);
        let month = Math.floor((h + L - 7 * m + 114) / 31);
        let day = ((h + L - 7 * m + 114) % 31) + 1;

        return new Date(year, month - 1, day);
    };

    /**
     * Adds a specified number of days to a date.
     * @param {Date} date - The original date.
     * @param {number} days - Number of days to add.
     * @returns {Date} - The new date.
     */
    const addDaysToDate = (date, days) => {
        let newDate = new Date(date);
        newDate.setDate(newDate.getDate() + days);
        return newDate;
    };

    /**
     * Generates a list of holiday dates for a given year.
     * @param {number} year - The year for which to generate holidays.
     * @returns {Object} - An object containing holiday names and their dates.
     */
    const getHolidays = (year) => ({
        'EASTER': getEasterDate(year),
        'NEW_YEAR': new Date(year, 0, 1),
        'EASTER_MONDAY': addDaysToDate(getEasterDate(year), 1),
        'LABOUR_DAY': new Date(year, 4, 1),
        'ASCENSION_DAY': addDaysToDate(getEasterDate(year), 39),
        'WHIT_MONDAY': addDaysToDate(getEasterDate(year), 50),
        'PENTECOST': addDaysToDate(getEasterDate(year), 49),
        'NATIONAL_HOLLIDAY': new Date(year, 6, 21),
        'ASSUMPTION_OF_MARY': new Date(year, 7, 15),
        'ALL_SAINTS_DAY': new Date(year, 10, 1),
        'ARMISCTICE_DAY': new Date(year, 10, 11),
        'CHRISTMAS': new Date(year, 11, 25),
    });

    /**
     * Returns the current week's dates formatted as "yyyy-mm-dd".
     * @returns {Array<string>} - Array of date strings for the current week.
     */
    const getCurrentWeekDates = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(startOfWeek.getDate() - (startOfWeek.getDay() + 6) % 7);

        const datesInWeek = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(date.getDate() + i);
            datesInWeek.push(formatDate(date));
        }

        return datesInWeek;
    };

    /**
     * Formats a Date object into "yyyy-mm-dd" string.
     * @param {Date} date - The date to format.
     * @returns {string} - Formatted date string.
     */
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    /**
     * Maps day numbers to their corresponding day names.
     * @param {string} dayNumber - The day number as a string ('0' to '7').
     * @returns {string|null} - The corresponding day name or null if invalid.
     */
    const mapDayNumberToName = (dayNumber) => {
        const dayMap = {
            '0': 'default',
            '1': 'monday',
            '2': 'tuesday',
            '3': 'wednesday',
            '4': 'thursday',
            '5': 'friday',
            '6': 'saturday',
            '7': 'sunday'
        };
        return dayMap[dayNumber] || null;
    };

    /**
     * Creates a standardized timeslot object.
     * @param {Object} timeslot - The original timeslot object.
     * @returns {Object} - The standardized timeslot.
     */
    const createTimeSlot = (timeslot) => ({
        from: timeslot.from_time,
        to: timeslot.to_time,
        title: timeslot.message?.message || '',
        desc: timeslot.message?.title || '',
        link: ''
    });

    /**
     * Processes an array of timeslots and returns hours and items arrays.
     * @param {Array} timeslots - Array of timeslot objects.
     * @param {boolean} multiple - Indicates if multiple timeslots are present.
     * @returns {Object} - Object containing hours and items arrays.
     */
    const processTimeslots = (timeslots, multiple = false) => {
        const hours = [];
        const items = [];
        timeslots.forEach(timeslot => {
            if (timeslot.message?.message && timeslots.length > 1) {
                const slot = createTimeSlot(timeslot);
                items.push(slot);
                if (!multiple) {
                    hours.push(slot);
                }
            } else if (timeslot.message?.message) {
                const slot = createTimeSlot(timeslot);
                items.push(slot);
                hours.push(slot);
            } else {
                hours.push({
                    from: timeslot.from_time,
                    to: timeslot.to_time,
                    title: timeslot.message?.title || '',
                    desc: timeslot.message?.message || ''
                });
            }
        });
        return { hours, items };
    };

    /**
     * Processes a record to determine opening hours and messages.
     * @param {Object|string} record - The record object or 'closed' string.
     * @returns {Object} - The processed opening hours object.
     */
    const checkRecord = (record) => {
        const oh = {
            hours: [],
            items: [],
            message: [],
            description: '',
            is_closed: false,
            appointment_only: false
        };

        if (record === 'closed' || record.closed) {
            oh.is_closed = true;
            if (record.message) {
                oh.message = [{ title: "", message: record.message }];
            }
        }

        if (record !== 'closed' && record.timeslots && record.timeslots.length > 0) {
            const timeslotData = processTimeslots(record.timeslots, record.timeslots.length > 1);
            oh.hours = timeslotData.hours;
            oh.items = timeslotData.items;
            if (record.messages) {
                oh.message = record.messages.map(mess => mess.message);
            }
        } else if (record !== 'closed' && !record.closed && record.exception_timeslots && record.exception_timeslots.length > 0) {
            if (record.message) {
                oh.message = [{ title: "", message: record.message }];
            }
            const reversedTimeslots = [...record.exception_timeslots].reverse();
            reversedTimeslots.forEach(timeslot => {
                if (timeslot.message?.message && record.exception_timeslots.length > 1) {
                    const slot = createTimeSlot(timeslot);
                    oh.items.push(slot);
                    oh.hours.push(slot);
                } else if (timeslot.message?.message) {
                    const slot = createTimeSlot(timeslot);
                    oh.items.push(slot);
                    oh.hours.push(slot);
                } else {
                    oh.hours.push({
                        from: timeslot.from_time,
                        to: timeslot.to_time,
                        title: '',
                        desc: ''
                    });
                    if (record.message) {
                        oh.message = [{ title: "", message: record.message }];
                    }
                }
            });
        } else if (!record.closed) {
            oh.message = record.message && record.message.descriptions
                .filter(message => message.language === lang)
                .map(message => message.description);
        }

        return oh;
    };

    /**
     * Processes exceptions for a timetable.
     * @param {Array} exceptions - Array of exception objects.
     * @param {Object} dayVariables - Object holding day-specific opening hours.
     * @param {Array} exception_days - Array to collect exception day names.
     * @param {Object} holidays - Object containing holiday dates.
     */
    const processExceptions = (exceptions, dayVariables, exception_days, holidays) => {
        const today = new Date();
        const currentWeekDates = getCurrentWeekDates();

        exceptions.forEach(exception => {
            let dateString = determineExceptionDate(exception, holidays, today);

            if (!dateString) return; // Skip if dateString is not determined

            const isWithinCurrentWeek = currentWeekDates.includes(dateString);
            if (!isWithinCurrentWeek) return;

            const date = new Date(dateString);
            const dayOfWeekStr = getDayOfWeekString(date);

            dayVariables[dayOfWeekStr] = checkRecord(exception);
            exception_days.push(dayOfWeekStr);
        });
    };

    /**
     * Determines the date string for an exception.
     * @param {Object} exception - The exception object.
     * @param {Object} holidays - The holidays object.
     * @param {Date} today - Today's date.
     * @returns {string} - The determined date string in "yyyy-mm-dd" format.
     */
    const determineExceptionDate = (exception, holidays, today) => {
        if (exception.holliday === 'ALL_HOLLIDAYS') {
            return findClosestHolidayDate(holidays, today);
        } else if (exception.holliday && exception.holliday !== '') {
            return formatDate(holidays[exception.holliday] || new Date(exception.date));
        } else {
            return exception.date;
        }
    };

    /**
     * Finds the closest holiday date to today.
     * @param {Object} holidays - The holidays object.
     * @param {Date} today - Today's date.
     * @returns {string|null} - The closest holiday date string or null.
     */
    const findClosestHolidayDate = (holidays, today) => {
        let closestDate = null;
        let minDifference = Infinity;
        for (let holiday in holidays) {
            if (holidays.hasOwnProperty(holiday)) {
                let holidayDate = holidays[holiday];
                let difference = Math.abs((holidayDate - today) / (1000 * 60 * 60 * 24)); // Difference in days

                if (difference < minDifference) {
                    minDifference = difference;
                    closestDate = holidayDate;
                }
            }
        }
        return closestDate ? formatDate(closestDate) : null;
    };

    /**
     * Gets the day of the week as a string.
     * @param {Date} date - The date object.
     * @returns {string} - Day of the week in lowercase (e.g., 'monday').
     */
    const getDayOfWeekString = (date) => {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return days[date.getDay()];
    };

    /**
     * Processes records for a timetable and updates dayVariables.
     * @param {Array} records - Array of record objects.
     * @param {Object} dayVariables - Object holding day-specific opening hours.
     * @returns {string|Object} - The default record or 'closed' state.
     */
    const processRecords = (records, dayVariables) => {
        let default_record = { hours: [], items: [], is_closed: false };

        records.forEach(record => {
            record.valid_days.forEach(day => {
                const dayName = mapDayNumberToName(day);

                if (dayName === 'default') {
                    if (record.timeslots.length > 0) {
                        default_record.hours.push(...checkRecord(record).hours);
                        default_record.items.push(...checkRecord(record).items);
                    }
                } else if (dayName) {
                    if (!dayVariables[dayName]) {
                        // ✅ Ensure initialization if dayVariables is empty
                        dayVariables[dayName] = { hours: [], items: [], is_closed: false };
                    }

                    const newRecord = checkRecord(record);

                    // ✅ Append timeslots instead of replacing them
                    // Merge timeslots while avoiding duplicates
                    newRecord.hours.forEach(newSlot => {
                        const existingSlotIndex = dayVariables[dayName].hours.findIndex(
                            slot => slot.from === newSlot.from && slot.to === newSlot.to
                        );
                        if (existingSlotIndex !== -1) {
                            // Update existing timeslot
                            dayVariables[dayName].hours[existingSlotIndex] = newSlot;
                        } else {
                            // Add new timeslot
                            dayVariables[dayName].hours.push(newSlot);
                        }
                    });

                    // Merge items while avoiding duplicates
                    newRecord.items.forEach(newItem => {
                        const existingItemIndex = dayVariables[dayName].items.findIndex(
                            item => item.from === newItem.from && item.to === newItem.to
                        );
                        if (existingItemIndex !== -1) {
                            dayVariables[dayName].items[existingItemIndex] = newItem;
                        } else {
                            dayVariables[dayName].items.push(newItem);
                        }
                    });


                    // ✅ Ensure `is_closed` status is correctly set
                    if (newRecord.is_closed) {
                        dayVariables[dayName].is_closed = true;
                    }
                }
            });
        });

        return default_record;
    };


    /**
     * Processes a single timetable.
     * @param {Object} timetable - The timetable object to process.
     * @returns {Object} - The processed timetable data.
     */
    const processTimetable = (timetable) => {
        const item = timetable.timetable;
        const dayVariables = initializeDayVariables();
        const exception_days = [];
        const currentYear = new Date().getFullYear();
        const holidays = getHolidays(currentYear);

        // Process Records
        const default_record = processRecords(item.records, dayVariables);

        // Determine Timetable Title
        // const timetable_title = item.timetable_type ? item.timetable_type.title : translate('opening_hours', lang);

        // Determine Timetable Title
        const timetable_title = item.timetable_type
            ? item.timetable_type[`title_${lang}`]
            : translate('opening_hours', lang);

        // Define 24/7 Record
        const record_24_7 = create24SevenRecord();

        // Process Exceptions
        processExceptions(item.day_exception, dayVariables, exception_days, holidays);

        // Compile Week Data
        const week = compileWeekData(item, dayVariables, default_record, record_24_7);

        // Compile Final Data
        return {
            exception_days: exception_days,
            title: timetable_title,
            is_24_7: item.is_24_7,
            appointment_only: item.appointment_only,
            message: item.messages ? item.messages.map(mess => mess.message) : "",
            week: week
        };
    };

    /**
     * Initializes the day variables object with default empty strings.
     * @returns {Object} - Initialized day variables.
     */
    const initializeDayVariables = () => ({
        monday: "",
        tuesday: "",
        wednesday: "",
        thursday: "",
        friday: "",
        saturday: "",
        sunday: "",
    });

    /**
     * Creates a 24/7 record object.
     * @returns {Object} - The 24/7 record.
     */
    const create24SevenRecord = () => ({
        "hours": [
            {
                "from": "00:00:00",
                "to": "23:59:59"
            }
        ],
        "items": [],
        "message": null,
        "description": "",
        "is_closed": false,
        "appointment_only": false
    });

    /**
     * Compiles the week data based on whether it's 24/7 or not.
     * @param {Object} item - The timetable item.
     * @param {Object} dayVariables - The day variables.
     * @param {string|Object} default_record - The default record.
     * @param {Object} record_24_7 - The 24/7 record.
     * @returns {Object} - The compiled week data.
     */
    const compileWeekData = (item, dayVariables, default_record, record_24_7) => {
        if (item.is_24_7) {
            return {
                monday: dayVariables.monday || record_24_7,
                tuesday: dayVariables.tuesday || record_24_7,
                wednesday: dayVariables.wednesday || record_24_7,
                thursday: dayVariables.thursday || record_24_7,
                friday: dayVariables.friday || record_24_7,
                saturday: dayVariables.saturday || record_24_7,
                sunday: dayVariables.sunday || record_24_7
            };
        } else {
            const closedRecord = checkRecord('closed');
            return {
                monday: dayVariables.monday || default_record || closedRecord,
                tuesday: dayVariables.tuesday || default_record || closedRecord,
                wednesday: dayVariables.wednesday || default_record || closedRecord,
                thursday: dayVariables.thursday || default_record || closedRecord,
                friday: dayVariables.friday || default_record || closedRecord,
                saturday: dayVariables.saturday || default_record || closedRecord,
                sunday: dayVariables.sunday || default_record || closedRecord,
            };
        }
    };

    /**
     * Processes all timetables of a specific type.
     * @param {Array} timetables - Array of timetable objects.
     * @returns {Array} - Array of processed timetable data.
     */
    const processAllTimetables = (timetables) => {
        return timetables.flatMap(timetable => processTimetable(timetable));
    };

    // Main Processing

    const processEstablishmentTimetables = (json) => {
        return json.establishment_timetables && json.establishment_timetables.length > 0
            ? processAllTimetables(json.establishment_timetables)
            : [];
    };

    const processEnterpriseTimetables = (json) => {
        return json.enterprise_timetables && json.enterprise_timetables.length > 0
            ? processAllTimetables(json.enterprise_timetables)
            : [];
    };

    const processGroupTimetables = (json) => {
        return json.group_timetables && json.group_timetables.length > 0
            ? processAllTimetables(json.group_timetables)
            : [];
    };

    const compileAllData = () => {
        const establishmentData = processEstablishmentTimetables(json);
        const enterpriseData = processEnterpriseTimetables(json);
        const groupData = processGroupTimetables(json);
        return [...establishmentData, ...enterpriseData, ...groupData];
    };

    return compileAllData();
};

export default OpeninghoursModel;
